import {
  DEFAULT_USER_SETTINGS,
  type BoardPreferences,
  type BoardRelationshipKind,
  type UserSettings,
  type UserSettingsPatch,
} from "@chessla/shared";
import { PRESETS } from "@/features/analysis/analysis-presets";
/** Shared preferences for every evidence board. */

export type ReviewEvaluationPreset = "quick" | "balanced" | "deep";
export interface ReviewBoardSettings {
  evaluationPreset: ReviewEvaluationPreset;
  useMaiaReplies: boolean;
  showLegalMoves: boolean;
  showMotifs: boolean;
  showPrincipleSignals: boolean;
  showOpponentAnalysis: boolean;
  showBestMoveArrow: boolean;
  showPositionMarkers: boolean;
  markerGroups: { pin: boolean; support: boolean };
  /** Relationship kinds the player switched off; hidden from board arrows and badges. */
  hiddenRelationships: BoardRelationshipKind[];
  showAllPositionMarkers: boolean;
  moveSounds: boolean;
  showEvaluation: boolean;
  showTacticalMotifs: boolean;
}

export type ReviewBoardSettingsPatch = Partial<Omit<ReviewBoardSettings, "markerGroups">> & {
  markerGroups?: Partial<ReviewBoardSettings["markerGroups"]>;
};
export const reviewBoardSettingsFromUserSettings = (settings: BoardPreferences): ReviewBoardSettings => ({
  evaluationPreset: settings.evaluationPreset,
  useMaiaReplies: settings.useMaiaReplies,
  showLegalMoves: settings.showLegalMoves,
  showMotifs: settings.showMotifs,
  showPrincipleSignals: settings.showPrincipleSignals,
  showOpponentAnalysis: settings.showOpponentAnalysis,
  showBestMoveArrow: settings.showBestMoveArrow,
  showPositionMarkers: settings.showPositionMarkers,
  markerGroups: { pin: settings.markerPins, support: settings.markerSupport },
  hiddenRelationships: settings.hiddenRelationships ?? [],
  showAllPositionMarkers: settings.showAllPositionMarkers,
  moveSounds: settings.moveSounds,
  showEvaluation: settings.showEvaluation,
  showTacticalMotifs: settings.showTacticalMotifs,
});
export type BoardPreferenceContext = "review" | "board" | "patterns" | "nikola";
export const BOARD_PREFERENCE_KEYS = {
  review: "gameBoardSettings",
  board: "freeBoardSettings",
  patterns: "patternBoardSettings",
  nikola: "nikolaBoardSettings",
} as const;
export const resolveBoardPreferences = (settings: UserSettings, context: BoardPreferenceContext): ReviewBoardSettings =>
  reviewBoardSettingsFromUserSettings({ ...settings, ...settings[BOARD_PREFERENCE_KEYS[context]] });

export const boardPreferencesPatch = (
  settings: UserSettings,
  context: BoardPreferenceContext,
  patch: ReviewBoardSettingsPatch,
): UserSettingsPatch => ({
  [BOARD_PREFERENCE_KEYS[context]]: {
    ...reviewBoardSettingsPatchToUserSettings(resolveBoardPreferences(settings, context)),
    ...reviewBoardSettingsPatchToUserSettings(patch),
  } as BoardPreferences,
});
export const reviewBoardSettingsPatchToUserSettings = ({
  markerGroups,
  ...settings
}: ReviewBoardSettingsPatch): UserSettingsPatch => ({
  ...settings,
  ...(markerGroups?.pin !== undefined ? { markerPins: markerGroups.pin } : {}),
  ...(markerGroups?.support !== undefined ? { markerSupport: markerGroups.support } : {}),
});
export const DEFAULT_REVIEW_BOARD_SETTINGS = reviewBoardSettingsFromUserSettings(DEFAULT_USER_SETTINGS);
export const REVIEW_EVALUATION_DEPTH: Record<ReviewEvaluationPreset, number> = { quick: 12, balanced: 16, deep: 24 };
export const REVIEW_PRESET_PRESENTATION = PRESETS.map(({ id, label, depth, icon }) => ({
  value: id as ReviewEvaluationPreset,
  label,
  detail: `Depth ${depth}`,
  icon,
}));
